import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import gsap from 'gsap';
import { fetchFaqs } from '../redux/slice/faq.slice';
import { Helmet } from 'react-helmet-async';
import FaqsPagePreview from '../components/legal/FaqsPagePreview';

const FALLBACK_FAQS = [
    { category: 'Account & Profile', q: 'Are the games permanent or subscription-based?', a: 'All games sold on CUBE GAMES are permanent purchases. Once you buy a game, you own the digital license forever. No recurring fees or subscriptions are required.', sortOrder: 0 },
    { category: 'Account & Profile', q: 'How do I reset my password?', a: 'Use the Forgot Password link on the login page to receive a reset email and set a new password.', sortOrder: 1 },
    { category: 'Purchases', q: 'Which payment methods are accepted?', a: 'We accept all major credit/debit cards, PayPal, and various secure digital wallets. All transactions are processed through encrypted gateways.', sortOrder: 0 },
    { category: 'Purchases', q: "Can I get a refund if the game doesn't run?", a: 'We offer refunds within 48 hours of purchase, provided the digital key has not been used or the game files have not been fully downloaded.', sortOrder: 1 },
    { category: 'Downloads', q: 'How do I receive my game after purchase?', a: "Immediately after checkout, your game key will appear in My Library. You can download files directly or activate the key on platforms like Steam, Epic, or Xbox.", sortOrder: 0 },
    { category: 'Downloads', q: 'How do I activate my game key?', a: "Copy the key from your library, open the target platform, select 'Activate a Product', and paste your key.", sortOrder: 1 },
    { category: 'Security', q: 'How is my account data protected?', a: 'We use industry-standard encryption to protect your data and keep your transactions safe.', sortOrder: 0 },
];

export default function FAQs() {
    const dispatch = useDispatch();
    const { faqs: dbFaqs, loading } = useSelector((state) => state.faq);

    useEffect(() => {
        dispatch(fetchFaqs());
    }, [dispatch]);

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.from('.faq-reveal', {
                y: 28,
                opacity: 0,
                duration: 0.75,
                stagger: 0.07,
                ease: 'power3.out',
                clearProps: 'all',
            });
        });
        return () => ctx.revert();
    }, []);

    const faqSchema = useMemo(() => {
        const source = dbFaqs.length > 0
            ? dbFaqs.filter((item) => item.isActive !== false)
            : FALLBACK_FAQS;
        return {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: source.map((f) => ({
                '@type': 'Question',
                name: f.question || f.q,
                acceptedAnswer: { '@type': 'Answer', text: f.answer || f.a },
            })),
        };
    }, [dbFaqs]);

    return (
        <>
            <Helmet>
                <title>Frequently Asked Questions | CUBE GAMES</title>
                <meta name="description" content="Get answers to your questions about game keys, digital delivery, payment methods, refund policies, and account security at Cube Games." />
                <meta name="keywords" content="gaming FAQ, game downloads support, help center, refund policy, key activation" />
                <link rel="canonical" href="https://cubegames.ca/faq" />
                <meta property="og:type" content="website" />
                <meta property="og:title" content="Frequently Asked Questions | CUBE GAMES" />
                <meta property="og:description" content="Get answers to your questions about game keys, digital delivery, payment methods, refund policies, and account security at Cube Games." />
                <meta property="og:url" content="https://cubegames.ca/faq" />
                <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
            </Helmet>

            <FaqsPagePreview
                faqs={dbFaqs}
                fallbackFaqs={FALLBACK_FAQS}
                loading={loading}
                revealClass="faq-reveal"
            />
        </>
    );
}
